import { BALANCE, type Cell, type Direction } from '@2k48/shared';

/**
 * Swipes and arrow keys.
 *
 * The rule that shapes this file: **input is never blocked by an animation.**
 * It does not check whether the board is busy, it does not queue, and it does
 * not drop. It reports the gesture the instant it completes, and the renderer
 * deals with whatever is in flight (see `BoardRenderer.finishRunning`).
 *
 * Queueing produces a board that replays a backlog seconds after the player
 * stopped touching it. Dropping produces a game that ignores you. Both feel
 * worse than a game that is simply slow.
 *
 * The one thing that *is* rate-limited is a flood inside a single frame, and
 * even that coalesces to the most recent gesture rather than discarding it.
 */

export interface InputHandlers {
  onDirection(direction: Direction): void;
  onTap?(cell: Cell | null, point: { x: number; y: number }): void;
}

export interface InputControllerOptions {
  /** Element that receives pointer events. Usually the entity layer. */
  target: HTMLElement;
  /** Maps a viewport point to a board cell, when the caller knows how. */
  cellAt?(point: { x: number; y: number }): Cell | null;
  handlers: InputHandlers;
}

const KEY_DIRECTIONS: Readonly<Record<string, Direction>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  KeyW: 'up',
  KeyS: 'down',
  KeyA: 'left',
  KeyD: 'right',
};

export class InputController {
  private readonly target: HTMLElement;
  private readonly handlers: InputHandlers;
  private readonly cellAt: InputControllerOptions['cellAt'];

  private pointerId: number | null = null;
  private startX = 0;
  private startY = 0;
  private startedAt = 0;

  private lastEmitAt = 0;
  private pending: Direction | null = null;
  private flushHandle = 0;

  private enabled = true;

  constructor(options: InputControllerOptions) {
    this.target = options.target;
    this.handlers = options.handlers;
    this.cellAt = options.cellAt;

    this.target.addEventListener('pointerdown', this.onPointerDown);
    this.target.addEventListener('pointermove', this.onPointerMove);
    this.target.addEventListener('pointerup', this.onPointerUp);
    this.target.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('keydown', this.onKeyDown);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  // --- pointer ---------------------------------------------------------------

  private readonly onPointerDown = (event: PointerEvent): void => {
    if (!this.enabled || this.pointerId !== null) return;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startedAt = performance.now();
    // Capture so a swipe that leaves the board still resolves.
    this.target.setPointerCapture(event.pointerId);
  };

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId) return;
    // Stops the page from taking over the gesture as a scroll. `touch-action:
    // none` on the layer does most of this; the call covers the rest.
    event.preventDefault();
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId) return;
    this.releasePointer(event.pointerId);

    if (!this.enabled) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    const elapsed = performance.now() - this.startedAt;
    const direction = resolveSwipe(dx, dy, elapsed);

    if (direction) {
      this.emit(direction);
      return;
    }

    const travel = Math.hypot(dx, dy);
    if (travel < BALANCE.input.swipeMinDistancePx && this.handlers.onTap) {
      const point = { x: event.clientX, y: event.clientY };
      this.handlers.onTap(this.cellAt?.(point) ?? null, point);
    }
  };

  private readonly onPointerCancel = (event: PointerEvent): void => {
    if (this.pointerId !== event.pointerId) return;
    this.releasePointer(event.pointerId);
  };

  private releasePointer(pointerId: number): void {
    this.pointerId = null;
    if (this.target.hasPointerCapture(pointerId)) {
      this.target.releasePointerCapture(pointerId);
    }
  }

  // --- keyboard --------------------------------------------------------------

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.enabled || event.repeat || event.metaKey || event.ctrlKey || event.altKey) return;

    const direction = KEY_DIRECTIONS[event.code];
    if (!direction) return;

    event.preventDefault();
    this.emit(direction);
  };

  // --- emission --------------------------------------------------------------

  /**
   * Emit now, unless two gestures landed inside the same handful of
   * milliseconds — in which case the newer one replaces the pending one and
   * goes out on the next frame. Coalescing, not queueing: at most one input is
   * ever waiting, and it is always the most recent.
   */
  private emit(direction: Direction): void {
    const now = performance.now();

    if (now - this.lastEmitAt >= BALANCE.input.minIntervalMs) {
      this.lastEmitAt = now;
      this.handlers.onDirection(direction);
      return;
    }

    this.pending = direction;
    if (this.flushHandle !== 0) return;

    this.flushHandle = window.setTimeout(
      () => {
        this.flushHandle = 0;
        const queued = this.pending;
        this.pending = null;
        if (queued) {
          this.lastEmitAt = performance.now();
          this.handlers.onDirection(queued);
        }
      },
      Math.max(0, BALANCE.input.minIntervalMs - (now - this.lastEmitAt)),
    );
  }

  destroy(): void {
    this.target.removeEventListener('pointerdown', this.onPointerDown);
    this.target.removeEventListener('pointermove', this.onPointerMove);
    this.target.removeEventListener('pointerup', this.onPointerUp);
    this.target.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('keydown', this.onKeyDown);
    if (this.flushHandle !== 0) window.clearTimeout(this.flushHandle);
    this.pending = null;
  }
}

/** Pure, so the thresholds can be reasoned about (and tested) on their own. */
export function resolveSwipe(dx: number, dy: number, elapsedMs: number): Direction | null {
  const { swipeMinDistancePx, swipeMaxCrossAxisRatio, swipeMaxDurationMs } = BALANCE.input;

  if (elapsedMs > swipeMaxDurationMs) return null;

  const absX = Math.abs(dx);
  const absY = Math.abs(dy);
  const dominant = Math.max(absX, absY);
  if (dominant < swipeMinDistancePx) return null;

  // A diagonal is ambiguous, and guessing wrong is worse than ignoring it.
  const cross = Math.min(absX, absY) / dominant;
  if (cross > swipeMaxCrossAxisRatio) return null;

  if (absX >= absY) return dx > 0 ? 'right' : 'left';
  return dy > 0 ? 'down' : 'up';
}
